import React, { useEffect, useRef, useState } from "react";

import { previewHistory, redoHistory, undoHistory } from "../useLearning";
import { HISTORY_ACTION, HISTORY_TEXT, errorText } from "./catalogText";

const C = HISTORY_TEXT.confirm;
const pct = (v) => Math.round(Number(v) || 0);

/**
 * The in-page confirm for undoing (or redoing) one case: loads the server's preview of the effect on the cause, says it as
 * "cause: 72% → 65% (−7 pts), cases 5 → 4" (with a note when the effect is small), asks for an undo reason from `reasons`
 * ({key, label}[]) and only then sends the undo or redo. `onDone(action, result)` runs after the server answered.
 */
export default function HistoryConfirm({ item, reasons, onDone, onCancel }) {
  const [preview, setPreview] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inFlight = useRef(false); // a double click lands before `busy` disables the button: one request per action
  const panel = useRef(null);

  useEffect(() => { if (panel.current) panel.current.focus(); }, []);

  useEffect(() => {
    let live = true;
    setPreview(null);
    setLoadError("");
    previewHistory(item.eid)
      .then((p) => {
        if (!live) return;
        if (p === null) setLoadError(HISTORY_TEXT.notConnected);
        else setPreview(p);
      })
      .catch((e) => { if (live) setLoadError(errorText(e)); });
    return () => { live = false; };
  }, [item.eid, attempt]);

  const action = preview ? preview.action : (item.undone ? HISTORY_ACTION.REDO : HISTORY_ACTION.UNDO);
  const isUndo = action === HISTORY_ACTION.UNDO;
  const canSubmit = Boolean(preview) && !busy && (!isUndo || Boolean(reason));

  const submit = async () => {
    if (inFlight.current || !canSubmit) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    try {
      const result = isUndo ? await undoHistory(item.eid, reason) : await redoHistory(item.eid);
      onDone(action, result);
    } catch (e) {
      setError(errorText(e));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const title = isUndo ? C.undoTitle : C.redoTitle;
  return (
    <div ref={panel} tabIndex={-1} role="group" aria-label={title} className="le-confirm">
      <h3 className="le-h">{title}</h3>
      {!preview && !loadError && <p className="le-muted" role="status">{C.loading}</p>}
      {loadError && (
        <div className="le-actions">
          <p className="le-error" role="alert">{loadError}</p>
          <button type="button" className="le-btn le-btn--sm" onClick={() => setAttempt((n) => n + 1)}>{C.retry}</button>
          <button type="button" className="le-btn le-btn--sm" onClick={onCancel}>{C.cancel}</button>
        </div>
      )}
      {preview && (
        <>
          <div className="le-control">
            <p className="le-cause-name">{C.effect({
              label: preview.cause_label, now: pct(preview.percent_now), after: pct(preview.percent_after),
              change: pct(preview.change_points), casesNow: preview.cases_now, casesAfter: preview.cases_after,
            })}</p>
            {preview.small_effect && <p className="le-muted">{C.smallEffect}</p>}
          </div>
          {isUndo && (
            <fieldset className="le-fieldset">
              <legend className="le-label">{C.reason}</legend>
              {reasons.length === 0 && <p className="le-error">{C.noReasons}</p>}
              <div className="le-choices">
                {reasons.map((r) => (
                  <label key={r.key} className="le-choice">
                    <input type="radio" name={`reason-${item.eid}`} value={r.key} checked={reason === r.key}
                      onChange={() => setReason(r.key)} />
                    {r.label}
                  </label>
                ))}
              </div>
              {reasons.length > 0 && !reason && <p className="le-muted">{C.reasonNeeded}</p>}
            </fieldset>
          )}
          {error && <p className="le-error" role="alert">{error}</p>}
          <div className="le-actions">
            <button type="button" className="le-btn le-btn--primary" disabled={!canSubmit} onClick={submit}>
              {busy ? C.working : (isUndo ? C.confirmUndo : C.confirmRedo)}
            </button>
            <button type="button" className="le-btn" disabled={busy} onClick={onCancel}>{C.cancel}</button>
          </div>
        </>
      )}
    </div>
  );
}
